import { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  ContentCopy,
  Email as EmailIcon,
  Event as EventIcon,
  ExpandMore,
  LinkedIn as LinkedInIcon,
} from "@mui/icons-material";
import apiClient from "@/api/client";
import { brand } from "@/theme";

interface Followup {
  id: string;
  company_id: string | null;
  individual_id: string | null;
  campaign_id: string | null;
  description: string;
  body: string | null;
  due_date: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "pending" | "in_progress" | "completed" | "cancelled";
  kind: "task" | "meeting" | null;
  meeting_starts_at: string | null;
  attendee_emails: string[] | null;
  calendar_event_id: string | null;
  assigned_to: string | null;
  completed_at: string | null;
  created_at: string;
  company_name: string | null;
  individual_name: string | null;
  individual_email: string | null;
  individual_linkedin_url: string | null;
  individual_source: string | null;
  campaign_name: string | null;
}

const PRIORITY_COLORS: Record<string, "default" | "info" | "warning" | "error"> = {
  low: "info",
  medium: "default",
  high: "warning",
  urgent: "error",
};

const STATUS_TABS = ["pending", "in_progress", "completed", "cancelled", ""] as const;
type StatusTab = (typeof STATUS_TABS)[number];

export default function CRMFollowups() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const campaignFilter = searchParams.get("campaign_id");

  const [statusFilter, setStatusFilter] = useState<StatusTab>("pending");
  const [groupBy, setGroupBy] = useState<"campaign" | "due_date">("campaign");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const { data, isLoading } = useQuery<Followup[]>({
    queryKey: ["crm-followups", statusFilter, campaignFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (campaignFilter) params.set("campaign_id", campaignFilter);
      const res = await apiClient.get<{ followups: Followup[] }>(
        `/crm/followups?${params.toString()}`,
      );
      return res.data.followups;
    },
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.put(`/crm/followups/${id}`, { status: "completed" });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["crm-followups"] }),
  });

  const today = new Date().toISOString().split("T")[0];

  // Group followups by campaign (or "Other" if no campaign) so a webinar
  // sequence's 4×N touches read as a cohesive block instead of a wall.
  const grouped = useMemo(() => {
    const groups = new Map<
      string,
      { label: string; campaignId: string | null; items: Followup[] }
    >();
    for (const f of data ?? []) {
      const key = groupBy === "campaign" ? (f.campaign_id ?? "__nocampaign__") : f.due_date;
      const label = groupBy === "campaign" ? (f.campaign_name ?? "Standalone tasks") : f.due_date;
      if (!groups.has(key)) {
        groups.set(key, { label, campaignId: f.campaign_id, items: [] });
      }
      groups.get(key)!.items.push(f);
    }
    return Array.from(groups.entries());
  }, [data, groupBy]);

  const copyBody = async (body: string) => {
    try {
      await navigator.clipboard.writeText(body);
      setSnackbar({ open: true, message: "Draft email copied to clipboard" });
    } catch {
      setSnackbar({ open: true, message: "Copy failed — select + copy manually" });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/crm")}>
          Back to CRM
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Follow-ups
          {campaignFilter && data && data.length > 0 && data[0].campaign_name && (
            <Typography variant="body2" color="text.secondary">
              Campaign: {data[0].campaign_name}
            </Typography>
          )}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center" }}>
        {STATUS_TABS.map((s) => (
          <Chip
            key={s || "all"}
            label={s || "All"}
            variant={statusFilter === s ? "filled" : "outlined"}
            color={statusFilter === s ? "primary" : "default"}
            onClick={() => setStatusFilter(s)}
            sx={{ textTransform: "capitalize" }}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Group by:
        </Typography>
        <Chip
          label="Campaign"
          size="small"
          variant={groupBy === "campaign" ? "filled" : "outlined"}
          onClick={() => setGroupBy("campaign")}
        />
        <Chip
          label="Date"
          size="small"
          variant={groupBy === "due_date" ? "filled" : "outlined"}
          onClick={() => setGroupBy("due_date")}
        />
      </Stack>

      {isLoading && <CircularProgress />}

      {data && data.length === 0 && <Alert severity="info">No follow-ups found.</Alert>}

      {grouped.map(([key, group]) => (
        <Accordion key={key} defaultExpanded sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {group.label}
              </Typography>
              <Chip label={`${group.items.length}`} size="small" />
              {group.campaignId && (
                <Button
                  size="small"
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaigns/${group.campaignId}`);
                  }}
                  sx={{ ml: "auto" }}
                >
                  Open campaign
                </Button>
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {group.items.map((f) => (
              <FollowupRow
                key={f.id}
                followup={f}
                today={today}
                onComplete={() => completeMutation.mutate(f.id)}
                onCopyBody={copyBody}
                completing={completeMutation.isPending}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />
    </Box>
  );
}

interface RowProps {
  followup: Followup;
  today: string;
  onComplete: () => void;
  onCopyBody: (body: string) => void;
  completing: boolean;
}

function FollowupRow({ followup: f, today, onComplete, onCopyBody, completing }: RowProps) {
  const isOverdue = f.status === "pending" && f.due_date < today;
  const isMeeting = f.kind === "meeting";
  // LinkedIn-source: surface deep-link + LinkedIn-flavoured chip so Evelina
  // can jump straight from the followup to the relevant DM thread / profile.
  const isLinkedInSource =
    !!f.individual_source && f.individual_source.toLowerCase().startsWith("linkedin");

  return (
    <Card
      sx={{
        mb: 1,
        borderLeft: `4px solid ${isOverdue ? "#f44336" : brand.forestGreen}`,
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
              {isMeeting && <EventIcon fontSize="small" sx={{ opacity: 0.6 }} />}
              <Typography variant="body1" fontWeight={600}>
                {f.description}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mb: 0.5 }}>
              <Chip label={f.priority} size="small" color={PRIORITY_COLORS[f.priority]} />
              <Chip
                label={`Due: ${f.due_date}`}
                size="small"
                color={isOverdue ? "error" : "default"}
                variant="outlined"
              />
              <Chip label={f.status} size="small" variant="outlined" />
              {f.company_name && (
                <Chip
                  label={f.company_name}
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to={`/crm/companies/${f.company_id}`}
                  clickable
                />
              )}
              {f.individual_name && (
                <Chip label={`👤 ${f.individual_name}`} size="small" variant="outlined" />
              )}
              {f.individual_email && (
                <Chip
                  icon={<EmailIcon />}
                  label={f.individual_email}
                  size="small"
                  variant="outlined"
                  component="a"
                  href={`mailto:${f.individual_email}`}
                  clickable
                />
              )}
              {isLinkedInSource && f.individual_linkedin_url && (
                <Chip
                  icon={<LinkedInIcon />}
                  label="Open in LinkedIn"
                  size="small"
                  color="primary"
                  variant="outlined"
                  component="a"
                  href={f.individual_linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                />
              )}
            </Stack>
          </Box>
          {f.status === "pending" && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<CheckCircle />}
              onClick={onComplete}
              disabled={completing}
            >
              Mark done
            </Button>
          )}
        </Stack>

        {f.body && (
          <Accordion
            sx={{ mt: 1, boxShadow: "none", "&:before": { display: "none" } }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ minHeight: 32, "& .MuiAccordionSummary-content": { my: 0 } }}
            >
              <Typography variant="caption" color="primary">
                Draft email — click to expand + copy
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ position: "relative" }}>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={() => onCopyBody(f.body!)}
                    sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box
                  sx={{
                    p: 1.5,
                    pr: 5,
                    bgcolor: "#f9f9f7",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    fontFamily: "'Source Sans 3', sans-serif",
                    fontSize: "0.85rem",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                  }}
                >
                  {f.body}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
                >
                  [BRACKETED] sections are deliberately blank — fill in your personal note before
                  sending.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
